import {
  AfterViewInit,
  ChangeDetectorRef,
  ComponentRef,
  DestroyRef,
  Directive,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  reflectComponentType,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import isEqual from 'fast-deep-equal';
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  map,
  merge,
  Observable,
  of,
  type OperatorFunction,
  pairwise,
  skipWhile,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs';

import { ElementsState, runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  BLAZE_ELEMENT_HOST_CLASS,
  BLAZE_HIDDEN_CLASS,
  CanvasElement,
  CanvasElementDataInputPayload,
  CanvasElementFrom,
  CanvasElementId,
  CanvasElementType,
  convertFormatToTableColumnFormat,
  ElementMetaPaths,
  getRenderSourcesByTarget,
  mergeProps,
  PartialPropsOf,
  PropsOf,
  PropsWithOverrides,
  RenderTarget,
  SlotId,
  SourceType,
  WrapperCommitButton,
} from '@blaze-runtime/engine-domain';
import {
  ColumnId,
  DurationColumnFormat,
  NumberPrecision,
} from '@blaze-shared/api-contracts-domain';
import { fromRouter, ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { UserAccessRolesService } from '@blaze-shared/auth-data-access';
import {
  ButtonElementMetaUiFeatures,
  CanvasElementDataProperties,
  CanvasElementInputBinding,
  CanvasElementPropertiesWithVisibility,
  CanvasElementWithDirtyFieldProps,
  ContainerActionConfig,
  ContainerElementMetaUiFeatures,
  ContainerFieldElementMetaUiFeatures,
  DEPRECATED_DURATION_FORMAT_MAPPINGS,
  FormFieldCondition,
  FormFieldConditionType,
  FormGroupActionType,
  getActiveBinding,
  InputElementMetaUiFeatures,
  isBindingActive,
  PropertiesConfigInputListCustomFieldModel,
  PropertiesConfigInputTableColumnModel,
  RenderElementContext,
  resolveUiFeature,
  RuntimeContext,
  STYLE_CONFIG_PROPERTIES_PATH,
  TableColumnFormat,
  ViewportMode,
} from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { loggerCreate } from '@blaze-shared/logger-util';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  createElementSchemaId,
  DATA_VALIDATION_EVENT_TYPE_EXCEPTIONS,
  DatasetColumn,
  DEFAULT_SCHEMA,
  findColumn,
  getColumnKey,
  isWorkflowSchema,
} from '@blaze-shared/schema-domain';
import { getOnPushChangeDetectorRef } from '@blaze-shared/shared-ui';
import {
  countedShareReplay,
  DateFormatOptions,
  DateTimeFormatOptions,
  deepClone,
  hashCodeFromObj,
  isDeepEqual,
  isEqualNoUndefined,
  isNullish,
  singularize,
  toResolvedColumnName,
} from '@blaze-shared/shared-util';
import { fromStyles } from '@blaze-shared/style-state-feature';
import { AppExtendedThemeValue, IThemeService, StyleSheet } from '@blaze-shared/theming-domain';
import { FormActionType, getValueByPath } from '@blaze-shared/workflow-domain';

import { triggerElementClick, updateElementProps } from '../../+state/elements/elements.actions';
import { getElements } from '../../+state/elements/elements.selectors';
import { ElementsMetaService, MetaFormData } from '../../data/elements-meta.service';
import { FormContainerPropsModel } from '../../elements/form-container/form-container.model';
import { CanvasElementControllerRegistry } from '../../services/canvas-element-controller-registry.service';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { ElementPropsService } from '../../services/element-props.service';
import { LayoutLoadingService } from '../../services/layout-loading.service';
import { LayoutLockService } from '../../services/layout-lock.service';
import { calculateNavMenuDirection } from '../../services/nav-menu-items.service';
import { RenderElementContextService } from '../../services/render-element-context.service';
import { RenderElementService } from '../../services/render-element.service';
import { SchemaValidationService } from '../../services/schema-validation.service';
import { ElementStylesService } from '../../services/styles';
import { ViewportService } from '../../services/viewport.service';
import { findPrimaryContainerForNavigationElement } from '../../utils/canvas-element/path';
import { checkViewportVisibility } from '../../utils/elements/element.utils';
import { inputVisibilityModelToExpression } from '../../utils/elements/expressions.utils';
import { findParentByElementType } from '../../utils/layout/relationship';
import { CanvasElementComponent, ComponentSlotData } from '../element-component';
import { CanvasElementDefinition } from '../element-definition';
import { arePropsWithVisibility } from '../element-model/are-props-with-visibility';
import {
  arePropsWithData,
  arePropsWithDirty,
  arePropsWithFeaturesConfig,
  getParentStyleSource,
  hasDataProps,
  hasProps,
  hasSlots,
  hasSlotStyleConfig,
  hasStylesConfig,
  hasWrapperRefs,
  slotHasProps,
} from '../element-model/element-props-guards';
import {
  isEditableForm,
  isFormGroupLike,
  isFormListLike,
  isNavigationElement,
  isPaginableElement,
  isVariantOfFormContainer,
} from '../element-model/element-type-guards';
import { getFormInputNameByType } from '../element-model/io';
import { mergeElementPropsByTarget } from '../element-model/props-overrides';
import { getViewportVisibilityFromProps } from '../element-model/visibility';
import { ElementIODataOrchestratorService } from '../io-data/element-io-data-orchestrator.service';
import { ElementInputProviderService } from '../io-data/inputs/element-input-data-provider.service';
import { RenderElementInstance } from '../models/render-element.models';
import { type ParentChildrenOverrides } from '../parent-overrides';

import { getPropsColumns } from './render-element/meta-features.utils';

const logDataFieldCalc = loggerCreate(
  'data_field_ui_features',
  null,
  'RenderElementDirective',
  null,
);

@UntilDestroy()
@Directive({
  selector: '[blazeRenderElement]',
  standalone: false,
})
export class RenderElementDirective implements OnInit, AfterViewInit, OnDestroy {
  private readonly actions = inject(Actions);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly userAccessRolesService = inject(UserAccessRolesService);
  private readonly elementInputProviderService = inject(ElementInputProviderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementContextService = inject(RenderElementContextService);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementPropsService = inject(ElementPropsService);
  private readonly elementsMetaService = inject(ElementsMetaService);
  private readonly intelliSenseService = inject(IntelliSenseService);
  private readonly elementStylesService = inject(ElementStylesService);
  private readonly layoutLoading = inject(LayoutLoadingService);
  private readonly layoutLockService = inject(LayoutLockService);
  private readonly schemaValidationService = inject(SchemaValidationService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store<ElementsState>);
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly themeService = inject(IThemeService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly viewportService = inject(ViewportService);
  private readonly elementControllerRegistry = inject(CanvasElementControllerRegistry);
  private readonly renderElementService = inject(RenderElementService);

  @Input('blazeRenderElementElementHostRef') elementHostRef!: HTMLElement;

  private readonly elementDataOrchestratorService: ElementIODataOrchestratorService;
  private readonly componentInitialized$$ = new BehaviorSubject<boolean>(false);
  private readonly componentSlotData$$ = new BehaviorSubject<ComponentSlotData | undefined>(
    undefined,
  );
  private readonly element$$ = new BehaviorSubject<CanvasElement | undefined>(undefined);
  private readonly elementContext$$ = new BehaviorSubject<RenderElementContext | undefined>(
    undefined,
  );
  /** Hide element based on viewport props */
  private readonly renderOnViewports$$ = new BehaviorSubject<ViewportMode[] | undefined>(undefined);
  private readonly wrappersChanged$$ = new Subject<CanvasElementId>();

  private readonly themeValue$ = this.themeService.activeTheme$.pipe(map((theme) => theme?.value));

  private changesCache: SimpleChanges = {};
  private componentRef?: ComponentRef<CanvasElementComponent>;
  private definedInputs = new Set<string>();
  private hasPropsWaitingForInit: PropsNeedUpdateResult | undefined = undefined;
  private updatePropSubscription?: Subscription;
  private viewChangeDetectorRef?: ChangeDetectorRef;

  @Input() set blazeRenderElement(value: CanvasElement | undefined) {
    if (!isEqualNoUndefined(this.element$$.value, value)) {
      this.element$$.next(value);
    }
  }

  @Input('blazeRenderElementElementContext') set elementContext(
    value: RenderElementContext | undefined,
  ) {
    if (!isEqual(this.elementContext$$.value, value)) {
      this.elementContext$$.next(value);
    }
  }

  private get instance(): RenderElementInstance | undefined {
    return this.componentRef?.instance as RenderElementInstance;
  }

  constructor() {
    this.elementDataOrchestratorService = new ElementIODataOrchestratorService(this.destroyRef);
  }

  ngOnInit(): void {
    this.initVisibility();
    this.initElementContextSpy();
  }

  ngAfterViewInit(): void {
    // Recreating component
    this.element$$
      .pipe(
        filter(Boolean),
        map((element) => ({
          element,
          definition: this.elementDefinitionsService.getDefinition(element?.type),
        })),
        concatLatestFrom(() => this.renderElementService.entities$),
        map(([elementData, entities]) => {
          const parentFormList = findParentByElementType(
            entities,
            elementData.element.id,
            'editableList' satisfies CanvasElementType,
          );
          return { elementData, parentFormList };
        }),
        concatMap(({ elementData, parentFormList }) =>
          !parentFormList
            ? of({ elementData, parentFormListId: undefined, parentSchema: undefined })
            : this.store
                .select(
                  runtimeSelectors.selectSchemaById(
                    parentFormList.id,
                    this.elementDefinitionsService.getDefaultOutputEventType(parentFormList.type),
                  ),
                )
                .pipe(
                  skipWhile((schema) => !schema),
                  take(1),
                  map((parentSchema) => ({
                    elementData,
                    parentFormListId: parentFormList.id,
                    parentSchema,
                  })),
                ),
        ),
        // If component not yet initialized or component changed
        filter(({ elementData, parentFormListId, parentSchema }) => {
          let parentCheck = true;
          if (parentFormListId) {
            parentCheck = !!parentSchema;
          }
          return (
            parentCheck &&
            (!this.componentRef || elementData.definition?.type !== this.instance?.elementType)
          );
        }),
        map(({ elementData }) => elementData),
        concatLatestFrom(({ element }) => {
          let styleSheet$ = of<StyleSheet | undefined>(undefined);
          if (hasStylesConfig(element) && !!element.props.styleConfig) {
            const { styleName, styleSheetId } = element.props.styleConfig;
            styleSheet$ = this.elementStylesService.observeElementStyles(
              styleSheetId,
              styleName,
              element.type,
            );
          }

          return [
            this.componentSlotData$$,
            this.store.select(fromRouter.getIsInEditor),
            this.elementContext$$,
            this.themeValue$,
            styleSheet$,
            this.elementStylesService.slotStyleSheets$,
            this.renderElementService.renderTarget$,
          ];
        }),
        untilDestroyed(this),
      )
      .subscribe(
        ([
          { element, definition },
          slotData,
          isInEditor,
          elementContext,
          themeValue,
          elementSheet,
          slotStyleSheets,
          renderTarget,
        ]) => {
          if (!definition) {
            throw new Error(`Could not find definition for ${element.type}`);
          }
          this.elementDataOrchestratorService.componentReinitialized(true);
          this.createComponent(
            element,
            definition,
            isInEditor,
            slotData,
            elementContext,
            themeValue,
            elementSheet,
            slotStyleSheets,
            renderTarget,
          );
        },
      );

    // Updating props
    combineLatest({
      componentSlotData: this.componentSlotData$$,
      element: this.element$$.pipe(filter(Boolean)),
      elementContext: this.elementContext$$,
      themeValue: this.themeValue$,
      // Keyed on the sheet config rather than the element: a prop edit cannot change which
      // sheet applies, and re-subscribing per edit only re-emits the same sheet. Type is part
      // of the key because it selects the sheet and can change under a live directive.
      elementSheet: this.element$$.pipe(
        map((e) =>
          hasStylesConfig(e) ? { styleConfig: e.props.styleConfig, type: e.type } : undefined,
        ),
        distinctUntilChanged(isDeepEqual),
        switchMap((sheetKey) =>
          sheetKey
            ? this.elementStylesService.observeElementStyles(
                sheetKey.styleConfig?.styleSheetId,
                sheetKey.styleConfig?.styleName,
                sheetKey.type,
              )
            : of(undefined),
        ),
      ),
      slotStyleSheets: this.elementStylesService.slotStyleSheets$,
      renderTarget: this.renderElementService.renderTarget$,
      // Trigger only - the bundle is not forwarded past the map below
      parentChildrenOverrides: this.observeParentChildrenOverrides(),
    })
      .pipe(
        untilDestroyed(this),
        filter(() => this.componentRef !== undefined),
        map(
          ({
            componentSlotData,
            element,
            elementContext,
            themeValue,
            elementSheet,
            slotStyleSheets,
            renderTarget,
          }) => ({
            componentSlotData,
            definition: this.elementDefinitionsService.getDefinition(element?.type),
            element,
            elementContext,
            themeValue,
            elementSheet,
            slotStyleSheets,
            renderTarget,
          }),
        ),
      )
      .subscribe(
        ({
          componentSlotData,
          definition,
          element,
          elementContext,
          themeValue,
          elementSheet,
          slotStyleSheets,
          renderTarget,
        }) => {
          this.updateComponent(
            element,
            definition as CanvasElementDefinition,
            componentSlotData,
            elementContext,
            themeValue,
            elementSheet,
            slotStyleSheets,
            renderTarget,
          );
        },
      );

    this.initOutputSchemaValidation();
  }

  ngOnDestroy(): void {
    const { id, type } = this.element$$.value ?? {};
    if (!id || !type) {
      return;
    }

    this.elementControllerRegistry.unregisterComponent(this.componentRef?.instance);
    this.elementDataOrchestratorService.cleanup();

    if (type === 'editableList' || type === 'formDetailsList') {
      this.elementInputProviderService.cleanupBaseProviders(id);
      this.elementsMetaService.clearElementsFormMetaBase(id);
    } else if (type === 'formContainer' || type === 'formDetails') {
      this.elementsMetaService.clearElementsFormMetaBase(id);
    } else {
      this.elementsMetaService.clearElementsMetaBase(id, type);
    }
  }

  /** Used by RenderElementHostDirective to inject the slot data */
  updateSlotData(componentSlotData: ComponentSlotData): void {
    this.componentSlotData$$.next(componentSlotData);
  }

  private createComponent<T extends CanvasElementType>(
    element: CanvasElement,
    elementDefinition: CanvasElementDefinition,
    isInEditor: boolean,
    componentSlotData?: ComponentSlotData,
    elementContext?: RenderElementContext,
    themeValue?: AppExtendedThemeValue | null,
    elementSheet?: StyleSheet,
    slotStyleSheets?: StyleSheet[],
    renderTarget: RenderTarget = 'desktop',
  ): void {
    // Cleanup/reset
    this.viewContainerRef.clear();
    this.viewContainerRef.createEmbeddedView(this.templateRef);

    if (this.updatePropSubscription) {
      this.updatePropSubscription?.unsubscribe();
    }

    // When element has some expressions to execute, it should be invisible at the beginning
    const visibilityDataProps = hasDataProps(element)
      ? element.props.data.find((data) => data.key === 'visible')
      : undefined;
    const shouldBeHiddenAtStart =
      isBindingActive(visibilityDataProps) && !!visibilityDataProps.dataSource?.expression?.length;
    this.elementDataOrchestratorService.isHiddenByBinding(shouldBeHiddenAtStart);
    this.renderOnViewports$$.next(
      getViewportVisibilityFromProps(hasProps(element) ? element.props : undefined),
    );

    // Create component
    this.componentRef = this.viewContainerRef.createComponent<CanvasElementComponent<T>>(
      elementDefinition.componentType,
    );

    const componentMetaData = reflectComponentType(this.componentRef.componentType);
    this.definedInputs.clear();
    componentMetaData?.inputs?.forEach((input) => this.definedInputs.add(input.propName));

    this.elementControllerRegistry.registerComponent(element, this.componentRef.instance);
    this.viewChangeDetectorRef = getOnPushChangeDetectorRef(this.componentRef);
    this.componentRef.setInput('ns', element.ns);
    this.componentRef.setInput('isInEditor', isInEditor);
    this.componentRef.setInput('elementId', element.id);
    this.componentRef.setInput('elementType', element.type);
    this.componentRef.setInput('ioConfig', elementDefinition.ioConfig);

    // Set component as initialized only after UI workflow attached to it has been executed,
    // or if there was no UI workflow to be executed (for which the uiWorkflowExecuted action
    // is also dispatched).
    if (!this.componentInitialized$$.value) {
      this.actions
        .pipe(
          ofType(runtimeActions.uiWorkflowExecuted),
          filter(({ elementId }) => elementId === element?.id),
          take(1),
        )
        .subscribe(() => {
          this.elementDataOrchestratorService.dataInitialized();
        });
    }
    this.componentInitialized$$.next(false); // reset flag, for when it's regenerated
    this.applyHostClass(this.componentRef.location.nativeElement);
    this.connectMetaFeatures(element, elementDefinition);
    this.initializeDataOrchestrator(element, elementDefinition);

    if (isInEditor) {
      this.connectToolbar(element, elementDefinition, elementContext);
    }
    this.connectPropUpdater(element);
    this.connectQueryData(element);
    this.connectViewportMode();
    this.connectAppResetHandler(elementDefinition);
    this.updateComponent(
      element,
      elementDefinition,
      componentSlotData,
      elementContext,
      themeValue,
      elementSheet,
      slotStyleSheets,
      renderTarget,
    );
    this.validateOutputSchemas(element);

    this.layoutLockService.unlocked$.subscribe(() => {
      this.store.dispatch(runtimeActions.executeUiWorkflow({ elementId: element.id }));
    });

    this.componentInitialized$$.next(true);
  }

  private onChanges(changes?: SimpleChanges): void {
    const instance = this.instance as unknown as OnChanges;
    changes = mergeChanges(this.changesCache, changes);

    /** Initial changes are emitted together */
    if (this.componentInitialized$$.value && Object.keys(changes).length) {
      if (instance.ngOnChanges) {
        instance.ngOnChanges(changes || {});
      }
      this.viewChangeDetectorRef?.markForCheck();
      this.componentRef?.changeDetectorRef.detectChanges();
      this.changesCache = {};
    }
  }

  private applyHostClass(htmlElement: HTMLElement): void {
    // Used for SCSS references
    htmlElement.classList?.add(BLAZE_ELEMENT_HOST_CLASS);
  }

  //#region connectMetaFeatures

  private connectMetaFeatures(
    element: CanvasElement,
    elementDefinition: CanvasElementDefinition,
  ): void {
    if (!elementDefinition.metaUiFeatures) {
      return;
    }

    const { paths, type: featuresType } = elementDefinition.metaUiFeatures;
    const inputName = elementDefinition.ioConfig?.inputs?.find(
      (i) => i.isDefaultDataValueInput,
    )?.name;
    if (!this.instance) {
      throw new Error(`no element instance (${element.id})`);
    }

    let features$:
      | Observable<
          | InputElementMetaUiFeatures
          | ButtonElementMetaUiFeatures
          | ContainerElementMetaUiFeatures<string>
        >
      | undefined = undefined;

    if (featuresType === 'input' || featuresType === 'container') {
      if (!inputName) {
        throw new Error(`no default value input (${element.id})`);
      }
      const dataInput$ = this.instance.dataInput$.pipe(map((di) => di?.[inputName]));
      if (featuresType === 'input') {
        features$ = this.connectInputElementMetaFeatures(
          element,
          elementDefinition,
          dataInput$,
          paths,
          inputName,
        );
      } else if (featuresType === 'container') {
        features$ = this.connectContainerMetaFeatures(dataInput$, paths, element.type);
      }
    } else if (featuresType === 'button') {
      const elements = syncSelect(this.store, getElements);
      const form = elements.find(
        (e) =>
          isEditableForm(e) &&
          hasWrapperRefs(e) &&
          Object.entries(e.props.wrapper.refs).find(
            ([ref, elId]) => elId === element.id && WrapperCommitButton.isRefId(ref),
          ),
      );
      features$ = form ? this.connectButtonElementMetaFeatures(element, form) : undefined;
    }

    features$
      ?.pipe(
        distinctUntilChanged(isDeepEqual),
        this.elementDataOrchestratorService.takeUntilDestroyedOrReloaded(),
        this.elementDataOrchestratorService.takeUntilDataChanged(),
      )
      .subscribe((uiFeatures) => {
        this.store.dispatch(
          runtimeActions.updateElementMeta({ elementId: element.id, meta: { uiFeatures } }),
        );
      });
  }

  private connectInputElementMetaFeatures(
    element: CanvasElement,
    elementDefinition: CanvasElementDefinition,
    dataInput$: Observable<CanvasElementDataInputPayload | undefined>,
    paths: ElementMetaPaths | undefined,
    inputName: CanvasElementInputBinding,
  ): Observable<InputElementMetaUiFeatures> {
    if (!this.instance) {
      throw new Error(`no element instance (${element.id})`);
    }

    const props$ = this.instance.props$.pipe(
      filter(Boolean),
      distinctUntilChanged(isDeepEqual),
      countedShareReplay(),
    );

    const elementFormBinding$ = props$.pipe(
      map((props) => {
        if (!arePropsWithData(props)) {
          return undefined;
        }
        return getActiveBinding(props.data as CanvasElementDataProperties[], inputName);
      }),
      distinctUntilChanged(isDeepEqual),
      countedShareReplay(),
    );

    const formData$: Observable<MetaFormData | undefined> = elementFormBinding$.pipe(
      switchMap((binding) => {
        const formId = binding?.dataSource?.id;
        if (
          !formId ||
          (binding?.dataSource?.sourceType !== 'formContainer' &&
            binding?.dataSource?.sourceType !== 'editableList')
        ) {
          return of(undefined);
        }
        return this.elementsMetaService.getOrCreateElementsFormMetaBase(formId, () =>
          combineLatest({
            renderTarget: this.renderElementService.renderTarget$,
            form: this.renderElementService.getElement(formId),
          }).pipe(
            map(({ renderTarget, form }) => {
              let result: MetaFormData | undefined = undefined;
              if (isVariantOfFormContainer(form)) {
                const props = mergeElementPropsByTarget(form, renderTarget);
                result = {
                  formId,
                  formOptions: {
                    markRequiredFields: props?.content?.options?.markRequiredFields,
                    submitOnEnter: props?.content?.options?.submitOnEnter,
                    changeOnBlur: props?.content?.options?.changeOnBlur,
                  },
                  formContentChildren: props?.content?.children,
                  formStyleChildren: props?.style?.children,
                  modelFields: props?.content?.model,
                  fieldsLabelGen: props?.featuresConfig?.fieldsLabelGen,
                };
              }
              if (isFormListLike(form)) {
                const props = mergeElementPropsByTarget(form, renderTarget);
                result = {
                  formId,
                  formOptions: undefined,
                  formContentChildren: undefined,
                  formStyleChildren: undefined,
                  modelFields: undefined,
                  fieldsLabelGen: props?.featuresConfig?.fieldsLabelGen,
                };
              }
              return result;
            }),
            distinctUntilChanged(isDeepEqual),
            countedShareReplay(),
          ),
        );
      }),
    );

    const conditionsWithBinding$: Observable<{
      binding: CanvasElementDataProperties | undefined;
      formData: MetaFormData | undefined;
      conditions?: {
        isRequired: boolean | undefined;
        isReadOnly: boolean | undefined;
        isInvalid: string | undefined;
      };
    }> = combineLatest({
      formData: formData$,
      binding: elementFormBinding$,
    }).pipe(
      switchMap(({ formData, binding }) => {
        if (!formData || !binding) {
          return of({ binding, formData });
        }

        const modelField = formData?.modelFields?.find(
          (f) => (f.fieldId ?? f.field) === binding.dataSource?.columns[0].id,
        );
        const conditions = modelField?.conditions;
        if (!conditions?.length) {
          return of({ binding, formData });
        }

        const conditionsHash = hashCodeFromObj(conditions);
        return this.elementsMetaService
          .getOrCreateElementsConditionsMetaBase(
            formData.formId,
            element.id,
            element.type,
            conditionsHash,
            () => {
              const runtimeCtx$ = this.elementInputProviderService
                .getExpressionRuntimeContext(element.id, element.type)
                .pipe(
                  distinctUntilChanged(isEqual),
                  map((context) => {
                    return this.intelliSenseService.normalizeLinkedParams(context);
                  }),
                  countedShareReplay(),
                );
              return combineLatest({
                isRequired: this.resolveFieldConditionQuery(runtimeCtx$, conditions, 'require'),
                isReadOnly: this.resolveFieldConditionQuery(runtimeCtx$, conditions, 'read-only'),
                isInvalid: this.resolveFieldConditionQuery(
                  runtimeCtx$,
                  conditions,
                  'validation',
                ).pipe(mapToErrorMessage(conditions)),
              }).pipe(
                distinctUntilChanged(isDeepEqual),
                startWith({
                  isRequired: undefined as boolean | undefined,
                  isReadOnly: undefined as boolean | undefined,
                  isInvalid: undefined as string | undefined,
                }),
                pairwise(),
                countedShareReplay(),
              );
            },
          )
          .pipe(
            map(([prev, next]) => {
              if (
                formData?.formId &&
                (prev.isRequired !== next.isRequired || prev.isInvalid !== next.isInvalid)
              ) {
                this.store.dispatch(
                  runtimeActions.formValidationTrigger({ formElementId: formData.formId }),
                );
              }
              return { binding, conditions: next, formData };
            }),
            countedShareReplay(),
          );
      }),
    );

    return this.elementsMetaService.getOrCreateElementMetaBase(element.id, element.type, () =>
      combineLatest({
        schema: dataInput$.pipe(
          map((di) => di?.schema),
          distinctUntilChanged(isDeepEqual),
        ),
        props: props$,
        cwb: conditionsWithBinding$,
      }).pipe(
        concatLatestFrom(({ props }) => {
          let styleName: string | undefined = undefined;
          let styleSheetId: string | undefined = undefined;
          if (STYLE_CONFIG_PROPERTIES_PATH in props) {
            styleName = props[STYLE_CONFIG_PROPERTIES_PATH]?.styleName;
            styleSheetId = props[STYLE_CONFIG_PROPERTIES_PATH]?.styleSheetId;
          }
          return [
            dataInput$.pipe(map((di) => di?.column)),
            this.themeValue$,
            this.elementStylesService.observeElementStyles(styleSheetId, styleName, element.type),
            this.renderElementService.renderTarget$,
          ];
        }),
        map(
          ([
            {
              schema,
              props,
              cwb: { conditions, binding, formData },
            },
            column,
            themeValue,
            styleSheet,
            renderTarget,
          ]) => {
            const { formId, formOptions, fieldsLabelGen } = formData ?? {};
            const dirty = (props as CanvasElementWithDirtyFieldProps)?.dirty;
            const isRequired = conditions?.isRequired ?? column?.isRequired ?? false;
            const propsIsReadonly = getValueByPath(
              props,
              paths?.readonly ?? 'content.options.readonly',
            ) as boolean;
            const isReadOnly = conditions?.isReadOnly ?? propsIsReadonly ?? false;
            const showIsRequired = formOptions?.markRequiredFields ?? false;
            const submitOnEnter = formOptions?.submitOnEnter ?? false;
            const changeOnBlur = formOptions?.changeOnBlur ?? false;

            const columnName = this.schemaService.replaceColumnIdsWithNames(
              binding?.bindings?.value ?? '',
            );
            const schemaColumn = schema?.columns?.find((col) => col.name === columnName);
            const schemaColumnName = this.schemaService.replaceColumnIdsWithNames(
              schemaColumn?.name ?? '',
            );
            const propsTitle = getValueByPath<string>(props, 'content.title.label') ?? undefined;
            const schemaTitle = toResolvedColumnName(
              column?.name ?? schemaColumnName ?? '',
              fieldsLabelGen,
            );
            const title = this.schemaService.replaceColumnIdsWithNames(
              resolveUiFeature(dirty?.label, propsTitle, schemaTitle),
            );

            const propsFormat = paths?.format
              ? (getValueByPath<TableColumnFormat>(props, paths?.format) ?? undefined)
              : undefined;
            const schemaFormat = convertFormatToTableColumnFormat(column?.format?.formatType);
            const format = resolveUiFeature(dirty?.format, propsFormat, schemaFormat);

            const propsPrecision = paths?.precision
              ? (getValueByPath<NumberPrecision>(props, paths?.precision) ?? undefined)
              : undefined;
            const precision = resolveUiFeature(
              dirty?.precision,
              propsPrecision,
              column?.precision,
              true,
            );

            const propsCurrency = paths?.currency
              ? (getValueByPath<string>(props, paths?.currency) ?? undefined)
              : undefined;
            const currency =
              resolveUiFeature(dirty?.currency, propsCurrency, column?.format?.currencySymbol) ??
              'USD';

            const propsDuration = paths?.duration
              ? (getValueByPath<DurationColumnFormat>(props, paths?.duration) ?? undefined)
              : undefined;
            let duration = resolveUiFeature(
              dirty?.duration,
              propsDuration,
              column?.format?.durationFormat,
            );
            if (duration) {
              duration = DEPRECATED_DURATION_FORMAT_MAPPINGS[duration] ?? duration;
            }

            const propsDateFormat = paths?.dateFormat
              ? (getValueByPath<DateFormatOptions>(props, paths?.dateFormat) ?? undefined)
              : undefined;
            const dateFormat = resolveUiFeature(dirty?.dateFormat, propsDateFormat, undefined);

            const propsDateTimeFormat = paths?.dateTimeFormat
              ? (getValueByPath<DateTimeFormatOptions>(props, paths?.dateTimeFormat) ?? undefined)
              : undefined;
            const dateTimeFormat = resolveUiFeature(
              dirty?.dateTimeFormat,
              propsDateTimeFormat,
              undefined,
            );

            const element = this.element$$.value;
            const propsTitleWidth =
              getValueByPath<string>(props, 'content.title.width') ?? undefined;
            let overrideTitleWidth: string | undefined;
            if (this.componentInitialized$$.value && element) {
              const runtimeProps = this.getRuntimeProps(
                element,
                elementDefinition.defaults,
                themeValue,
                styleSheet,
                renderTarget,
              );
              overrideTitleWidth =
                getValueByPath<string>(runtimeProps, 'content.title.width') ?? undefined;
              if (!isEqualNoUndefined(this.instance?.props, runtimeProps)) {
                this.componentRef?.setInput('props', runtimeProps);
              }
            }
            const titleWidth = resolveUiFeature(
              dirty?.labelWidth,
              propsTitleWidth,
              overrideTitleWidth,
            );

            const result: InputElementMetaUiFeatures = {
              column,
              title,
              titleWidth,
              precision,
              format,
              currency,
              duration,
              dateFormat,
              dateTimeFormat,
              isRequired,
              isReadOnly,
              isInvalid: conditions?.isInvalid,
              showIsRequired,
              submitOnEnter,
              changeOnBlur,
              formElementId: formId,
            };

            logDataFieldCalc('info', 'connectInputElementMetaFeatures', (tag) =>
              console.log(`${tag} 🟩🟨 ${element?.type} | ${element?.id} | ${result.title}:`, {
                result,
                props,
                column,
                formData,
                conditions,
                label: { isDirty: dirty?.label, props: propsTitle, schema: schemaTitle },
                labelWidth: {
                  isDirty: dirty?.labelWidth,
                  props: propsTitleWidth,
                  override: overrideTitleWidth,
                },
                format: { isDirty: dirty?.format, props: propsFormat, schema: schemaFormat },
                precision: {
                  isDirty: dirty?.precision,
                  props: propsPrecision,
                  schema: column?.precision,
                },
                currency: {
                  isDirty: dirty?.currency,
                  props: propsCurrency,
                  schema: column?.format?.currencySymbol,
                },
                duration: {
                  isDirty: dirty?.duration,
                  props: propsDuration,
                  schema: column?.format?.durationFormat,
                },
                dateFormat: {
                  isDirty: dirty?.dateFormat,
                  props: propsDateFormat,
                  schema: undefined,
                },
                dateTimeFormat: {
                  isDirty: dirty?.dateTimeFormat,
                  props: propsDateTimeFormat,
                  schema: undefined,
                },
              }),
            );

            return result;
          },
        ),
        countedShareReplay(),
      ),
    );
  }

  private connectContainerMetaFeatures(
    dataInput$: Observable<CanvasElementDataInputPayload | undefined>,
    paths: ElementMetaPaths | undefined,
    elementType: CanvasElementType,
  ): Observable<ContainerElementMetaUiFeatures<string>> {
    if (!this.instance) {
      throw new Error('no element instance');
    }

    return combineLatest({
      schema: dataInput$.pipe(
        map((di) => di?.schema),
        distinctUntilChanged(),
        startWith(undefined),
        pairwise(),
        // allow when: there was no schema, or there was a schema, and a new came in
        filter(([prev, next]) => !prev || (!!prev && !!next)),
        map(([_, next]) => next),
      ),
      props: this.instance.props$.pipe(filter(Boolean)),
      element: this.element$$.pipe(filter(Boolean)),
    }).pipe(
      map(({ element, schema, props }) => {
        const propsTitle = getValueByPath(props, 'content.title.label') as string;
        let isTitleDirty: boolean | undefined = undefined;

        const propsColumns = getPropsColumns(props, paths?.columns);

        if (arePropsWithDirty(props)) {
          const dirty = (props as CanvasElementWithDirtyFieldProps).dirty;
          if (dirty) {
            isTitleDirty = dirty.label;
          }
        }

        let schemaLabel = '';
        let schemaColumns: DatasetColumn[] | undefined = undefined;
        if (schema) {
          schemaLabel =
            (isWorkflowSchema(schema) ? schema.tableName : schema.name) ??
            (schema.name !== '$$input1' ? schema.name : undefined) ??
            '';
          if (isVariantOfFormContainer(element) && schemaLabel) {
            schemaLabel = singularize(schemaLabel);
          }
          schemaColumns = schema.columns;

          const fProps = props as FormContainerPropsModel;
          if (
            schemaLabel &&
            (elementType === 'formContainer' || elementType === 'formDetails') &&
            fProps.content.origin === 'generated'
          ) {
            let prefix = '';
            switch (fProps.content.action) {
              case 'insert':
                prefix = 'Add ';
                break;
              case 'update':
                prefix = 'Edit ';
                break;
              case 'view':
                prefix = 'View ';
                break;
              default:
                break;
            }
            schemaLabel = `${prefix}${schemaLabel}`;
          }
        }

        const title = resolveUiFeature(isTitleDirty, propsTitle, schemaLabel);
        const fieldsLabelGen = arePropsWithFeaturesConfig(props)
          ? props.featuresConfig.fieldsLabelGen
          : undefined;

        const columns = Object.entries(propsColumns).flatMap(
          ([fieldType, columns]) =>
            columns?.map((propsCol) => {
              const propsLabel: string | undefined = propsCol.label;
              const propsPrecision: NumberPrecision | undefined = propsCol.precision;
              const propsCurrency: string | undefined = propsCol.currency;
              const propsDuration: DurationColumnFormat | undefined = propsCol.duration;
              const propsDateFormat: string | undefined = propsCol.dateFormat;
              const propsDateTimeFormat: string | undefined = propsCol.dateTimeFormat;

              let propsFormat: TableColumnFormat | undefined;
              let columnId: ColumnId | undefined;

              switch (elementType) {
                case 'table': {
                  const cProps = propsCol as PropertiesConfigInputTableColumnModel;
                  propsFormat = cProps.format;
                  columnId = getColumnKey(cProps) as ColumnId;
                  break;
                }
                case 'list':
                case 'ganttChart':
                case 'kanbanBoard':
                case 'map': {
                  const cProps = propsCol as PropertiesConfigInputListCustomFieldModel;
                  propsFormat = cProps.displayFormat;
                  columnId = cProps.column as ColumnId;
                  break;
                }
              }

              const columnDirty =
                propsCol && 'dirty' in propsCol
                  ? (propsCol as CanvasElementWithDirtyFieldProps).dirty
                  : undefined;
              const schemaColumn = columnId
                ? findColumn(schemaColumns, { id: columnId })
                : undefined;

              const schemaName = toResolvedColumnName(schemaColumn?.name ?? '', fieldsLabelGen);
              const schemaFormat = convertFormatToTableColumnFormat(
                schemaColumn?.format?.formatType,
              );
              const schemaPrecision = schemaColumn?.precision;
              const schemaCurrency = schemaColumn?.format?.currencySymbol;
              const schemaDuration = schemaColumn?.format?.durationFormat;

              const result: ContainerFieldElementMetaUiFeatures<string> = {
                fieldType,
                id: columnId,
                name: resolveUiFeature(columnDirty?.label, propsLabel, schemaName),
                format: resolveUiFeature(
                  columnDirty?.format,
                  propsFormat,
                  schemaFormat,
                ) as TableColumnFormat,
                precision: resolveUiFeature(
                  columnDirty?.precision,
                  propsPrecision,
                  schemaPrecision,
                  true,
                ),
                currency:
                  resolveUiFeature(columnDirty?.currency, propsCurrency, schemaCurrency) ?? 'USD',
                duration: resolveUiFeature(columnDirty?.duration, propsDuration, schemaDuration),
                dateFormat: resolveUiFeature(columnDirty?.dateFormat, propsDateFormat, undefined),
                dateTimeFormat: resolveUiFeature(
                  columnDirty?.dateTimeFormat,
                  propsDateTimeFormat,
                  undefined,
                ),
                column: propsCol,
              };

              logDataFieldCalc('debug', 'connectContainerMetaFeatures', (tag) =>
                console.log(`${tag} 🟦🟨 ${element.type} | ${element.id} | ${result.name}:`, {
                  result,
                  props,
                  schemaColumn,
                  schemaColumns,
                  label: { isDirty: columnDirty?.label, props: propsLabel, schema: schemaName },
                  format: {
                    isDirty: columnDirty?.format,
                    props: propsFormat,
                    schema: schemaFormat,
                  },
                  precision: {
                    isDirty: columnDirty?.precision,
                    props: propsPrecision,
                    schema: schemaPrecision,
                  },
                  currency: {
                    isDirty: columnDirty?.currency,
                    props: propsCurrency,
                    schema: schemaCurrency,
                  },
                  duration: {
                    isDirty: columnDirty?.duration,
                    props: propsDuration,
                    schema: schemaDuration,
                  },
                  dateFormat: {
                    isDirty: columnDirty?.dateFormat,
                    props: propsDateFormat,
                    schema: undefined,
                  },
                  dateTimeFormat: {
                    isDirty: columnDirty?.dateTimeFormat,
                    props: propsDateTimeFormat,
                    schema: undefined,
                  },
                }),
              );

              return result;
            }) ?? [],
        );

        const result: ContainerElementMetaUiFeatures<string> = { title, columns };

        logDataFieldCalc('info', 'connectContainerMetaFeatures', (tag) =>
          console.log(`${tag} 🟦 ${element.type} | ${element.id}:`, {
            result,
            label: { isTitleDirty, propsTitle, schemaLabel },
            props,
            schema,
          }),
        );

        return result;
      }),
    );
  }

  private connectButtonElementMetaFeatures(
    element: CanvasElement,
    parentForm: CanvasElement,
  ): Observable<ButtonElementMetaUiFeatures> {
    if (!this.instance) {
      throw new Error(`no element instance (${element.id})`);
    }

    const formData$ = this.renderElementService.getElement(parentForm.id).pipe(
      map((formElement) => {
        return {
          requiredDisableSubmit:
            hasProps<PropsOf<'formContainer'>>(formElement) &&
            formElement.props.content?.options?.requiredDisableSubmit,
        };
      }),
    );

    return formData$.pipe(
      map((formData) => {
        const result = {
          requiredDisableSubmit: formData.requiredDisableSubmit,
        };

        logDataFieldCalc('info', 'connectButtonElementMetaFeatures', (tag) =>
          console.log(`${tag} 🟩🟪 ${element?.type} | ${element?.id}:`, {
            result,
          }),
        );

        return result;
      }),
    );
  }

  private resolveFieldConditionQuery(
    normalizedContext$: Observable<RuntimeContext>,
    conditions: FormFieldCondition[],
    type: FormFieldConditionType,
  ): Observable<boolean | undefined> {
    const query = conditions.find((c) => c.type === type)?.query;
    if (!query?.conditions.length) {
      return of(undefined);
    }
    const expr = inputVisibilityModelToExpression(query);
    return normalizedContext$.pipe(
      map((normalizedContext) => {
        return this.intelliSenseService.evalExpressionObj(expr, normalizedContext, true);
      }),
    );
  }

  //#endregion

  private initializeDataOrchestrator(
    element: CanvasElement,
    elementDefinition: CanvasElementDefinition,
  ): void {
    const elementInstance = this.componentRef?.instance;
    if (!elementInstance) {
      console.log(`RenderElement [${element.id}|${element.type}]: missing elementInstance`);
      return;
    }
    this.elementDataOrchestratorService.configure({
      element,
      elementInstance,
      componentInitialized$: this.componentInitialized$$,
      elementContext$: this.elementContext$$,
      wrappersChanged$: this.wrappersChanged$$,
      elementDefinition,
      setComponentInput: (input, value) => {
        this.componentRef?.setInput(input, value);
      },
      changesNotifier: (changes) => this.onChanges(changes),
    });
  }

  private connectToolbar(
    element: CanvasElement,
    elementDefinition: CanvasElementDefinition,
    elementCtx?: RenderElementContext,
  ): void {
    if (!elementDefinition.toolbar?.includes('triggerClick')) {
      return;
    }

    this.actions
      .pipe(
        this.elementDataOrchestratorService.untilDestroyedOrReloaded(),
        ofType(triggerElementClick),
        filter(
          ({ elementId, elementContext }) =>
            elementId === element.id &&
            (!elementContext ||
              elementContext?.rowContext?.rowId === elementCtx?.rowContext?.rowId),
        ),
      )
      .subscribe(() => {
        if (typeof this.instance?.emitClick === 'function') {
          this.instance.emitClick(true);
        }
      });
  }

  private connectPropUpdater(element: CanvasElement): void {
    if (!this.instance?.updateProp) {
      return;
    }

    this.updatePropSubscription = this.instance.updateProp
      .pipe(this.elementDataOrchestratorService.untilDestroyedOrReloaded())
      .subscribe((req) => {
        this.store.dispatch(
          updateElementProps({
            elementId: element.id,
            path: req.path,
            value: req.payload,
          }),
        );
      });
  }

  private connectQueryData(element: CanvasElement): void {
    if (!isPaginableElement(element)) {
      return;
    }
    if (!this.instance) {
      throw new Error(`no element instance (${element.id})`);
    }

    this.instance.queryState$ = this.store
      .select(runtimeSelectors.selectQueryByElementId(element.id))
      .pipe(distinctUntilChanged(isEqual));
    this.instance.paginationPending$ = this.store
      .select(runtimeSelectors.selectPendingPaginationsByElementId(element.id))
      .pipe(
        map((request) => !!request),
        distinctUntilChanged(),
      );
  }

  private connectViewportMode(): void {
    this.viewportService.mode$
      .pipe(this.elementDataOrchestratorService.untilDestroyedOrReloaded())
      .subscribe((mode) => {
        this.componentRef?.setInput('viewportMode', mode);
      });
  }

  /**
   * Connects reset handler for elements that have outputs.
   * Every element should emit empty / default value when app needs to be reset.
   * It can happen when user is switching account.
   * @param elementDefinition
   * @private
   */
  private connectAppResetHandler(elementDefinition: CanvasElementDefinition): void {
    if (!elementDefinition.ioConfig?.outputs) {
      return;
    }
    this.actions
      .pipe(
        ofType(runtimeActions.clearOutputsAndSchema),
        this.elementDataOrchestratorService.untilDestroyedOrReloaded(),
      )
      .subscribe(() => {
        this.instance?.reset?.();
      });
  }

  /**
   * The children-overrides bundle this element's styling parent contributes.
   *
   * Exists as a re-render trigger: a styled child's own entity does not change when its
   * parent's styling does, and no other source in the props pipeline watches the parent. The
   * overlay itself is recomputed in {@link getRuntimeProps}.
   *
   * Keyed on the child's id and its `parentStyleSource` marker rather than on the element,
   * because both are written once when the parent generates the child. Keying it on the
   * element would rebuild the inner stream on every child prop edit and reset its dedupe,
   * costing one emission per edit for a bundle that did not change. The parent's own props
   * stay inside the inner stream, so the role it grants the child is re-read whenever they
   * change - a step that stops pointing at the child drops its styling on the next emission.
   */
  private observeParentChildrenOverrides(): Observable<ParentChildrenOverrides | undefined> {
    return this.element$$.pipe(
      map((element) => ({
        childId: element?.id,
        source: getParentStyleSource(hasProps(element) ? element.props : undefined),
      })),
      distinctUntilChanged(isDeepEqual),
      switchMap(({ childId, source }) => {
        if (!source || !childId) {
          return of(undefined);
        }

        return combineLatest({
          parent: this.renderElementService.getElement(source.elementId),
          renderTarget: this.renderElementService.renderTarget$,
        }).pipe(
          map(({ parent, renderTarget }) =>
            this.elementPropsService.resolveParentChildrenOverrides(parent, childId, renderTarget),
          ),
          distinctUntilChanged(isDeepEqual),
        );
      }),
    );
  }

  //#region updateComponent

  private updateComponent(
    element: CanvasElement,
    elementDefinition: CanvasElementDefinition,
    componentSlotData?: ComponentSlotData,
    elementContext?: RenderElementContext,
    themeValue?: AppExtendedThemeValue | null,
    elementSheet?: StyleSheet,
    slotStyleSheets?: StyleSheet[],
    renderTarget: RenderTarget = 'desktop',
  ): void {
    if (!this.instance) {
      return;
    }
    this.elementDataOrchestratorService.updateParams({
      element,
      elementDefinition,
    });
    const componentInitialized = this.componentInitialized$$.value;

    if (this.instance.elementId !== element.id) {
      this.componentRef?.setInput('elementId', element.id);
    }

    if (!isEqual(this.instance.elementContext, elementContext)) {
      this.componentRef?.setInput('elementContext', elementContext);
    }

    if (hasProps(element)) {
      const combined = this.getRuntimeProps(
        element,
        elementDefinition.defaults,
        themeValue,
        elementSheet,
        renderTarget,
      );

      let propsUpdate = needsPropsUpdate(element, this.instance, combined);

      if (propsUpdate.propsChanged) {
        this.componentRef?.setInput('props', combined);
        if (!componentInitialized) {
          this.hasPropsWaitingForInit = propsUpdate;
        }
      }

      if (this.hasPropsWaitingForInit && componentInitialized) {
        propsUpdate = { ...propsUpdate, ...this.hasPropsWaitingForInit };
        this.hasPropsWaitingForInit = undefined;
      }

      if (propsUpdate.propsChanged && componentInitialized) {
        const resetData = propsUpdate.dataBindingsChanged || propsUpdate.formActionChanged;
        const resetChildren = propsUpdate.wrapperRefsChanged || propsUpdate.formActionChanged;

        if (resetData) {
          this.elementDataOrchestratorService.dataChanged(element.id);
        }
        if (resetChildren) {
          this.wrappersChanged$$.next(element.id);
        }

        if (resetData) {
          this.connectMetaFeatures(element, elementDefinition);
        }
        this.elementDataOrchestratorService.reconnect({ resetData, resetChildren });
        if (propsUpdate.viewportVisibilityChanged) {
          this.renderOnViewports$$.next(getViewportVisibilityFromProps(this.instance.props));
        }

        this.componentInitialized$$.next(true);
      }
    }

    if (hasSlots(element)) {
      if (needsSlotsUpdate(this.instance, componentSlotData)) {
        this.componentRef?.setInput('slots', componentSlotData);
        this.handleSlotHosting(componentSlotData);
      }

      /**
       * Not all elements that have slots defined need props for them.
       * Update it only if these components need slots props.
       */
      if (this.definedInputs.has('slotsProps')) {
        const slotsProps = this.makeSlotsProps(
          element,
          elementDefinition,
          themeValue,
          slotStyleSheets ?? [],
          renderTarget,
        );

        this.componentRef?.setInput('slotsProps', slotsProps);
      }
    }
  }

  private getRuntimeProps<T extends CanvasElementType>(
    element: CanvasElementFrom<T>,
    defaults: PropsOf<T> | undefined,
    themeValue: AppExtendedThemeValue | undefined | null,
    elementSheet: StyleSheet | undefined | null,
    renderTarget: RenderTarget,
  ): PropsOf<T> | undefined {
    if (!hasProps(element)) {
      return;
    }

    const isGlobalSheet = fromStyles.isStyleGlobal(elementSheet);
    const styleData = fromStyles.mapStyleDataToProps(elementSheet?.data ?? []);
    let localSheet: PropsWithOverrides | undefined;
    let globalSheet: PropsWithOverrides | undefined;
    if (isGlobalSheet) {
      globalSheet = styleData;
    } else {
      localSheet = styleData;
    }
    const renderSources = getRenderSourcesByTarget[renderTarget ?? 'desktop'](
      element.props,
      element.overrides,
      localSheet?.props,
      localSheet?.overrides,
    );

    if (!themeValue) {
      throw new Error('Theme value is required for rendering element');
    }

    const runtimeOverrides = this.resolvePropsRuntimeOverrides(
      element as any,
      this.elementContext$$.value ?? ({} as any),
      element.props as any,
      renderTarget,
    );

    const merged = mergeProps(
      [
        { value: defaults, source: SourceType.DEFAULTS },
        ...renderSources,
        { value: globalSheet?.props, source: SourceType.GLOBAL_STYLE },
        {
          value: runtimeOverrides,
          source: SourceType.OVERRIDES,
        },
      ],
      themeValue,
    );

    /**
     * Some elements are mutating the props object.
     * Temporary fix.
     * TODO: Try to find all functions that are trying to mutate this object
     *       and refactor them so they don't.
     */
    return deepClone(merged) as PropsOf<T>;
  }

  private resolvePropsRuntimeOverrides(
    element: CanvasElement,
    ec: RenderElementContext,
    props: any,
    renderTarget: RenderTarget,
  ): any {
    const elementType = element.type;
    let propsOverrides: Record<string, any> | undefined = {};

    // Handle form list rows
    if (!isNullish(ec.rowContext?.rowId)) {
      const patchInit = ec.showTitle ? { content: { title: { enabled: true } } } : {};
      const patch = this.elementDefinitionsService.getPropsBuilder(elementType, patchInit);
      if (!patch) {
        return patchInit;
      }

      patch.setPlaceholder(
        ec.showTitle ? '' : props?.content?.input?.placeholder || props?.content?.title?.label,
      );

      propsOverrides = { ...propsOverrides, ...patch.build() };
    }

    if (elementType === 'navMenu') {
      const navMenuProps = props as PartialPropsOf<'navMenu'>;
      const newDirection = calculateNavMenuDirection(navMenuProps, ec);
      if (navMenuProps.content?.autoDirection === true && !!ec.navSection) {
        propsOverrides = { ...propsOverrides, content: { direction: newDirection } };
      }
    }

    if (
      ec?.disableShadowEffect &&
      this.elementDefinitionsService.getFeature(elementType, 'visualStyle') === 'block'
    ) {
      const patch = this.elementDefinitionsService.getPropsBuilder(elementType);
      const visualEffect = this.elementDefinitionsService
        .getPropsExtractor(elementType, props)
        ?.getVisualEffect();

      if (patch && !visualEffect) {
        patch.setVisualEffect({ type: 'none' });
        propsOverrides = { ...propsOverrides, ...patch.build() };
      }
    }

    if (arePropsWithData(props)) {
      const binding = getActiveBinding(
        props.data as CanvasElementDataProperties[],
        getFormInputNameByType(element.type),
      );
      if (binding?.dataSource?.sourceType === 'formContainer') {
        propsOverrides = this.elementPropsService.applyParentOverrides({
          parentId: binding.dataSource.id,
          childId: element.id,
          elementType: element.type,
          elementProps: mergeElementPropsByTarget(element, renderTarget) ?? {},
          patchInitProps: propsOverrides,
          renderTarget,
        });
      }
    }

    // Generated children without a form binding (e.g. the wizard's question/explanation
    // Text elements) point at their styling parent through the persisted marker instead
    const parentStyleSource = getParentStyleSource(props);
    if (parentStyleSource) {
      propsOverrides = this.elementPropsService.applyParentOverrides({
        parentId: parentStyleSource.elementId,
        childId: element.id,
        elementType: element.type,
        elementProps: mergeElementPropsByTarget(element, renderTarget) ?? {},
        patchInitProps: propsOverrides,
        renderTarget,
      });
    }

    return propsOverrides;
  }

  private makeSlotsProps(
    element: CanvasElement,
    elementDefinition: CanvasElementDefinition,
    themeValue: AppExtendedThemeValue | undefined | null,
    slotStyleSheets: StyleSheet[],
    renderTarget: RenderTarget,
  ): Record<SlotId, PropsOf<CanvasElementType>> | undefined {
    if (!hasSlots(element)) {
      return;
    }

    if (!themeValue) {
      throw new Error('Theme value is required for rendering slot');
    }

    return element.slots.ids.reduce<Record<SlotId, PropsOf<CanvasElementType>>>(
      (acc, slotId) => {
        const slot = element.slots[slotId];
        let styleData;

        if (slotStyleSheets.length && hasSlotStyleConfig(slot)) {
          const slotSheet = fromStyles.findMatchingStyleSheet({
            id: slot.props.styleConfig?.styleSheetId,
            name: slot.props.styleConfig?.styleName,
            elementType: element.type,
            sheets: slotStyleSheets,
          });

          styleData = fromStyles.mapStyleDataToProps(slotSheet?.data ?? []);
        }

        if (slot && slotHasProps(slot)) {
          const renderSources = getRenderSourcesByTarget[renderTarget ?? 'desktop'](
            slot.props,
            slot.overrides,
            styleData?.props,
            styleData?.overrides,
          );

          acc[slotId] = mergeProps(
            [
              { value: elementDefinition?.slotDefaults, source: SourceType.DEFAULTS },
              ...renderSources,
            ],
            themeValue,
          );
        }

        return acc;
      },
      {} as Record<SlotId, PropsOf<CanvasElementType>>,
    );
  }

  /** If the slot is configured to host a layout (nested layout) - trigger loading of that layout */
  private handleSlotHosting(componentSlotData?: ComponentSlotData): void {
    // Return if no hosting happens
    if (!componentSlotData?.slotIds.some((id) => componentSlotData.slotNodesData[id].hosting)) {
      return;
    }

    const namespacesToLoad = componentSlotData?.slotIds
      .map((id) => componentSlotData.slotNodesData[id]?.hosting?.ns)
      .filter((ns) => ns !== undefined);

    if (namespacesToLoad?.length > 0) {
      namespacesToLoad.forEach((ns) => {
        this.layoutLoading.markLayoutAsNeeded(ns);
      });
    }
  }

  //#endregion

  private initElementContextSpy(): void {
    combineLatest({
      elementContext: this.elementContext$$,
      elementId: this.element$$.pipe(map((e) => e?.id)),
    })
      .pipe(
        distinctUntilChanged(isEqual),
        untilDestroyed(this),
        // When the element gets destroyed, dispose the stored element context
        finalize(() =>
          this.elementContextService.clear(
            this.element$$.value?.id,
            this.elementContext$$.value?.rowContext,
          ),
        ),
      )
      .subscribe(({ elementContext, elementId }) => {
        this.elementContextService.set(elementId, elementContext);
      });
  }

  private initOutputSchemaValidation(): void {
    if (!this.schemaValidationService.shouldValidate) {
      return;
    }

    // Resets the ready inputs when the component is reinitialized or the data changed
    merge(
      this.elementDataOrchestratorService.dataChanged$,
      this.elementDataOrchestratorService.componentReinitialized$,
    )
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (!this.element$$.value) {
          console.warn('Element not found when resetting data inputs');
          return;
        }

        // Reset the ready inputs
        this.elementDataOrchestratorService.dataInputsReady({});
        this.validateOutputSchemas(this.element$$.value);
      });
  }

  private initVisibility(): void {
    const visibilityProps$: Observable<CanvasElementPropertiesWithVisibility | undefined> =
      this.element$$.pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        switchMap((element) => {
          if (!isNavigationElement(element)) {
            return of(element?.id);
          }

          return this.renderElementService.entityState$.pipe(
            map((elementsState) => {
              return findPrimaryContainerForNavigationElement(elementsState, element)?.id;
            }),
            first(Boolean),
          );
        }),
        switchMap((elementId) => {
          return elementId ? this.renderElementService.getElement(elementId) : of(undefined);
        }),
        map((element) => {
          if (!hasProps(element) || !arePropsWithVisibility(element.props)) {
            return undefined;
          }
          return element.props;
        }),
        countedShareReplay(),
      );

    const renderOnViewports$ = visibilityProps$.pipe(
      map((props) => getViewportVisibilityFromProps(props)),
    );

    const renderInRoles$ = combineLatest({
      props: visibilityProps$,
      userAccessRoles: this.userAccessRolesService.userAccessRoles$,
    }).pipe(
      map(({ props }) => {
        if (!props) {
          return true;
        }
        const { accessRoles } = props.content?.visible ?? {};
        return accessRoles?.length
          ? this.userAccessRolesService.isCurrentUserInAccessRoles(accessRoles)
          : true;
      }),
    );

    combineLatest({
      shouldHide: this.elementDataOrchestratorService.isHiddenByBinding$,
      viewportMode: this.viewportService.mode$,
      renderOnViewports: renderOnViewports$,
      renderInRoles: renderInRoles$,
      isInEditor: this.store.select(fromRouter.getIsInEditor),
    })
      .pipe(
        map(({ shouldHide, viewportMode, renderOnViewports, renderInRoles, isInEditor }) => {
          const isInViewport = checkViewportVisibility(viewportMode, renderOnViewports);

          // In view mode - hide the element completely so it does not take up space
          // renderInRoles - hide elements visible to given access roles
          // Elements that are hidden in viewport will not be visible in editor
          const isViewHidden = !isInViewport || (!isInEditor && (!renderInRoles || shouldHide));
          return isViewHidden;
        }),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((isViewHidden) => {
        this.elementHostRef.classList.toggle(BLAZE_HIDDEN_CLASS, isViewHidden);
      });
  }

  /**
   * Validates if all outputs provided a schema directly initialization
   *
   * Makes sure the schema is emitted when inputs are ready (the data has been passed to the component)
   * @param element
   */
  private validateOutputSchemas(element: CanvasElement): void {
    const ioConfig = this.elementDefinitionsService.getIoConfig(element.type);
    const outputConfigs = ioConfig?.outputs ?? [];

    const outputConfigDetails = outputConfigs
      .filter(({ eventType }) => !DATA_VALIDATION_EVENT_TYPE_EXCEPTIONS.includes(eventType))
      .map(({ eventType, schema }) => ({
        defaultSchema: schema,
        schemaKey: createElementSchemaId(element.id, eventType),
      }));

    // Find the element in state to ensure it exists (is ready to be analyzed for output schemas)
    const elementInState$ = this.element$$.pipe(
      map((element) => element?.id),
      distinctUntilChanged(),
      filter(Boolean),
      switchMap((elementId) => this.renderElementService.getElement(elementId)),
      filter(Boolean),
    );

    /**
     * Wait for all bound data inputs to be ready, and then check if the component emitted the schema for each output
     */
    combineLatest({
      dataInputsReady: this.elementDataOrchestratorService.dataInputsReady$,
      dataProps: elementInState$.pipe(
        map((element) => (hasDataProps(element) ? element.props.data : undefined)),
      ),
    })
      .pipe(
        map(({ dataInputsReady, dataProps }) => {
          if (!dataProps || dataProps.length === 0) {
            return true;
          }
          if (!dataInputsReady) {
            return false;
          }

          const boundInputs = dataProps.filter((data) => isBindingActive(data));
          const allInputsReady = boundInputs.every((data) => {
            // We shouldn't wait for non-blocking inputs as they're optional and are not required for the component to initialize
            const isNonBlockingInput = ioConfig?.inputs?.find(
              (input) => input.name === data.key,
            )?.isNonBlockingInput;
            return isNonBlockingInput || dataInputsReady[data.key] === true;
          });

          return allInputsReady;
        }),
        debounceTime(0), // Delay to make sure the data is emitted and processed
        first((allInputsReady) => allInputsReady === true),
        concatLatestFrom(() => this.store.select(runtimeSelectors.selectSchemas)),
        this.elementDataOrchestratorService.untilDestroyedOrReloaded(),
        this.elementDataOrchestratorService.untilDataChanged(),
      )
      .subscribe(([, schemas]) => {
        const missingSchemas = outputConfigDetails
          .filter(({ schemaKey, defaultSchema }) => {
            // The schema is explicitly set to null - it means there should be no data passed (`query` event is an exception)
            // Handle outputs with no schema - no data is enforced in the output data validation part
            if (defaultSchema === null) {
              return false;
            }

            // Otherwise, check if the schema is actually missing or still using a placeholder DEFAULT_SCHEMA
            // which means it was not properly declared.
            return !schemas[schemaKey] || isDeepEqual(schemas[schemaKey], DEFAULT_SCHEMA);
          })
          .map(({ schemaKey }) => schemaKey);

        if (missingSchemas.length) {
          // TODO: add Sentry logging once it's close to being stable
          console.error(
            `[Schema Validation] Missing initial output schemas for element ${element.type} ${element.id} - ${missingSchemas.join(', ')}`,
          );
        }
      });
  }
}

function mergeChanges(changesCache: SimpleChanges, changes?: SimpleChanges): SimpleChanges {
  if (changes) {
    for (const [key, change] of Object.entries(changes)) {
      changesCache[key] = change;
    }
  }
  return changesCache;
}

interface PropsNeedUpdateResult {
  propsChanged: boolean;
  dataBindingsChanged: boolean;
  wrapperRefsChanged?: boolean;
  formActionChanged?: boolean;
  viewportVisibilityChanged?: boolean;
}

/**
 * @param component component instance that might need to be updated
 * @param props the up to date props
 * @returns true if props have been updated
 */
function needsPropsUpdate<T extends CanvasElementType>(
  element: CanvasElementFrom<T>,
  component: CanvasElementComponent<T>,
  props: PropsOf<T>,
): PropsNeedUpdateResult {
  if (!component.props && !props) {
    return { propsChanged: false, dataBindingsChanged: false };
  }
  if ((!component.props && props) || (component.props && !props)) {
    return { propsChanged: true, dataBindingsChanged: true, viewportVisibilityChanged: true };
  }

  const prevFormAction = getFormAction(element, component.props as any);
  const newFormAction = getFormAction(element, props as any);
  const formActionChanged = prevFormAction !== newFormAction;

  const viewportVisibilityChanged = !isEqualNoUndefined(
    arePropsWithVisibility(component.props) && component.props.content?.visible?.viewports,
    arePropsWithVisibility(props) && props.content?.visible?.viewports,
  );

  const componentDataProps = arePropsWithData(component.props) ? component.props.data : undefined;
  const newDataProps = arePropsWithData(props) ? props.data : undefined;

  return {
    propsChanged: !isEqualNoUndefined(component.props, props),
    dataBindingsChanged: !isEqualNoUndefined(componentDataProps, newDataProps),
    wrapperRefsChanged: !isEqualNoUndefined(component.props?.wrapper, props?.wrapper),
    formActionChanged,
    viewportVisibilityChanged,
  };
}

/** Checks whether the slots have changed */
function needsSlotsUpdate(
  component: CanvasElementComponent,
  slotData?: ComponentSlotData,
): boolean {
  if (!slotData) {
    return false;
  }

  if (!isEqual(component.slots?.slotIds, slotData.slotIds)) {
    return true;
  }

  if (component?.slots?.templates) {
    const componentSlotsTemplatesKeys = Object.keys(component.slots.templates) as SlotId[];
    // Compare length
    if (componentSlotsTemplatesKeys.length !== Object.keys(slotData.templates).length) {
      return true;
    }

    for (const slotId of componentSlotsTemplatesKeys) {
      if (
        // Compare references to the templates
        slotData.templates[slotId] !== component.slots.templates[slotId] ||
        // Compare references to the slot nodes data
        slotData.slotNodesData[slotId] !== component.slots.slotNodesData[slotId]
      ) {
        return true;
      }
    }
  }

  return false;
}

function getFormAction<T extends CanvasElementType>(
  element: CanvasElementFrom<T>,
  props: PartialPropsOf<T> | undefined,
): FormActionType | FormGroupActionType | ContainerActionConfig | undefined {
  if (!isEditableForm(element) && !isFormGroupLike(element)) {
    return undefined;
  }

  // Make sure the correct props are there
  if (
    !props ||
    !('content' in props) ||
    !props.content ||
    !('action' in props.content) ||
    !props.content.action
  ) {
    return undefined;
  }

  return props.content.action;
}

function mapToErrorMessage(
  conditions: FormFieldCondition[],
): OperatorFunction<boolean | undefined, string | undefined> {
  return map((invalid) => {
    if (!invalid) {
      return undefined;
    }
    return conditions.find((c) => c.type === 'validation')?.error ?? 'This field is invalid';
  });
}
